import React, { useState } from 'react';
import { useUser } from '@/shared/hooks/useUser';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronDown, Menu, Moon, Sun } from 'lucide-react';
import { logout } from '@/shared/api/authentication-api';
import { useToast } from '@/shared/hooks/use-toast';
import { NotificationsBell } from '@/features/notifications';
import { useSandboxMode } from '@/features/sandbox';
import { useCurrentClient } from '@/shared/hooks/useCurrentClient';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { useAllUserProjects } from '@/shared/hooks/useUserProjects';
import { updateUserProfile } from '@/features/my-account/api/profile-api';
import { Project } from '@/types/base';
import { IronviewLogo } from '@/shared/components/IronviewLogo';

// Import shadcn/ui components
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';

interface TopbarProps {
  onMenuClick: () => void;
}

const ProjectSwitcher: React.FC = () => {
  const { user } = useUser();
  const { currentClient } = useCurrentClient();
  const { inSandboxMode } = useSandboxMode();
  const navigate = useNavigate();
  const {
    projectIdNumber,
    routeProjectName,
    setSelectedProjectId,
    clearSelectedProject,
  } = useCurrentProject();
  // No fallback literal here: when the client isn't resolved yet (or the
  // user has none), passing undefined makes useAllUserProjects use the
  // user-scoped /base/user/client-projects/ endpoint instead of requesting
  // a client literally named after the fallback string.
  const clientName = currentClient?.name ?? user?.client_info?.name;
  const { allProjects, isLoading } = useAllUserProjects(clientName);

  const projects = React.useMemo(
    () =>
      allProjects
        .filter((project) => (inSandboxMode ? Boolean(project.is_sandbox) : !project.is_sandbox))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    [allProjects, inSandboxMode],
  );

  const activeProjects = React.useMemo(
    () => projects.filter((project) => project.is_active),
    [projects],
  );
  const completedProjects = React.useMemo(
    () => projects.filter((project) => !project.is_active),
    [projects],
  );

  const selectedProject = React.useMemo(() => {
    if (projectIdNumber != null) {
      return projects.find((project) => project.id === projectIdNumber);
    }
    if (routeProjectName) {
      return projects.find(
        (project) => decodeURIComponent(project.name) === decodeURIComponent(routeProjectName),
      );
    }
    return undefined;
  }, [projectIdNumber, projects, routeProjectName]);

  const handleSelectProject = (project: Project) => {
    if (!clientName) return;
    setSelectedProjectId(project.id);
    navigate(`/app/property/${clientName}/${project.id}/dashboard`);
  };

  const handleClearProject = () => {
    clearSelectedProject();
    // Without a resolved client, land on the dashboard redirect which
    // resolves the right place from the user's profile.
    navigate(clientName ? `/app/dashboard/${clientName}` : '/app');
  };

  const renderProjectItem = (project: Project) => (
    <DropdownMenuItem key={project.id} onClick={() => handleSelectProject(project)}>
      <span className="flex h-4 w-4 items-center justify-center">
        {selectedProject?.id === project.id && (
          <Check className="h-4 w-4 text-primary" strokeWidth={1.9} />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate">{project.name}</span>
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="min-w-0 max-w-[280px] flex-1 justify-between gap-2 px-2 shadow-none sm:gap-3 sm:px-3.5"
          aria-label={selectedProject ? `Select project: ${selectedProject.name}` : 'Select project'}
          title={selectedProject?.name}
        >
          <span className="flex min-w-0 items-center gap-2 sm:gap-2.5">
            <Building2 className="h-4 w-4 shrink-0 text-primary" strokeWidth={1.9} />
            <span className="truncate text-[13.5px]">
              {selectedProject?.name ?? 'Select project'}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.9} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[min(22rem,calc(100vw-2rem))]">
        <DropdownMenuLabel>Select a project</DropdownMenuLabel>
        <DropdownMenuItem onClick={handleClearProject}>
          <span className="flex h-4 w-4 items-center justify-center">
            {!selectedProject && <Check className="h-4 w-4 text-primary" strokeWidth={1.9} />}
          </span>
          All projects
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="max-h-[320px] overflow-y-auto slate-scroll">
          {isLoading ? (
            <DropdownMenuItem disabled>Loading projects...</DropdownMenuItem>
          ) : projects.length === 0 ? (
            <DropdownMenuItem disabled>No projects available</DropdownMenuItem>
          ) : (
            <>
              {activeProjects.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    Active
                  </DropdownMenuLabel>
                  {activeProjects.map(renderProjectItem)}
                </>
              )}
              {completedProjects.length > 0 && (
                <>
                  {activeProjects.length > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    Completed
                  </DropdownMenuLabel>
                  {completedProjects.map(renderProjectItem)}
                </>
              )}
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Topbar: React.FC<TopbarProps> = ({ onMenuClick }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [isSavingTheme, setIsSavingTheme] = useState(false);

  React.useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'));
  }, [user?.prefers_dark]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      // Clear localStorage token if it exists
      localStorage.removeItem('token');
      
      toast({
        title: "Success",
        description: "Logged out successfully",
        variant: 'default',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
      
      // Redirect to homepage and reload to clear user state
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Error",
        description: "Failed to logout. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
      setIsLoggingOut(false);
    }
  };

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.first_name) return 'U';
    const firstInitial = user.first_name[0]?.toUpperCase() || '';
    const lastInitial = user.last_name?.[0]?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  const toggleTheme = async () => {
    if (isSavingTheme) return;
    const nextDark = !document.documentElement.classList.contains('dark');
    document.documentElement.classList.toggle('dark', nextDark);
    setIsDark(nextDark);
    setIsSavingTheme(true);

    try {
      await updateUserProfile({ prefers_dark: nextDark });
    } catch (error) {
      document.documentElement.classList.toggle('dark', !nextDark);
      setIsDark(!nextDark);
      toast({
        title: 'Display preference not saved',
        description: 'Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setIsSavingTheme(false);
    }
  };

  return (
    <header aria-label="Application toolbar" className="sticky top-0 z-30 min-w-0 shrink-0 border-b border-border bg-card/90 backdrop-blur-xl dark:bg-background/80">
      <div className="flex h-[73px] min-w-0 items-center gap-2 px-3 sm:gap-3.5 sm:px-6 lg:px-9">
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
          {/* Mobile menu button */}
          <Button
            variant="outline"
            size="icon"
            className="shrink-0 text-muted-foreground lg:hidden"
            onClick={onMenuClick}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-5 w-5" strokeWidth={1.9} />
          </Button>

          {/* Logo */}
          <div className="hidden shrink-0 items-center md:flex">
            <IronviewLogo className="max-h-[30px] w-[12rem]" />
          </div>
          <ProjectSwitcher />
        </div>

        {/* Right: User profile dropdown */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="relative hidden rounded-full text-muted-foreground hover:text-foreground min-[380px]:inline-flex"
            onClick={toggleTheme}
            disabled={isSavingTheme}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun className="h-5 w-5" strokeWidth={1.9} /> : <Moon className="h-5 w-5" strokeWidth={1.9} />}
          </Button>
          <NotificationsBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex shrink-0 items-center gap-2.5 rounded-full p-[5px] shadow-none lg:pr-3">
                <span className="sr-only">Open user menu</span>
                <Avatar className="h-[31px] w-[31px]">
                  <AvatarImage
                    src={(user as any)?.photo || "/media/images/user_thumbnails/avatar.png"}
                    alt="Profile"
                  />
                  <AvatarFallback>
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden items-center gap-2 lg:flex">
                  <span className="max-w-24 truncate text-[13.5px] font-medium text-foreground xl:max-w-32">
                    {user?.first_name || 'Admin'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" strokeWidth={1.9} />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/app/account')}>
                My Account
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="text-destructive focus:text-destructive"
              >
                {isLoggingOut ? 'Logging out...' : 'Log Out'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Topbar; 
